import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IBlockScreen, defaultValue } from 'app/shared/model/block-screen.model';

const initialState: EntityState<IBlockScreen> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/block-screens';

// Actions

interface IPagedBlockScreenQueryParams extends IQueryParams {
  nameSearch?: string;
}

export const getEntities = createAsyncThunk('blockScreen/fetch_entity_list', async ({ page, size, sort, nameSearch }: IPagedBlockScreenQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&search=${nameSearch}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IBlockScreen[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'blockScreen/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBlockScreen>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntityByTruckImplementId = createAsyncThunk(
  'blockScreen/fetch_entity_by_truck_implement_id',
  async (truckImplementId: string | number) => {
    const requestUrl = `${apiUrl}/truck-implement/${truckImplementId}`;
    return axios.get<IBlockScreen>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'blockScreen/create_entity',
  async (entity: IBlockScreen, thunkAPI) => {
    const result = await axios.post<IBlockScreen>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'blockScreen/update_entity',
  async (entity: IBlockScreen, thunkAPI) => {
    const result = await axios.put<IBlockScreen>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'blockScreen/partial_update_entity',
  async (entity: IBlockScreen, thunkAPI) => {
    const result = await axios.patch<IBlockScreen>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'blockScreen/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBlockScreen>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const BlockScreenSlice = createEntitySlice({
  name: 'blockScreen',
  initialState,
  reducers: {
    reset(state) {
      state.updateSuccess = false;
      state.loading = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getEntityByTruckImplementId.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntityByTruckImplementId), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BlockScreenSlice.actions;

// Reducer
export default BlockScreenSlice.reducer;
